import * as Yup from "yup";
import { Box, Button, Divider, Text, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ActionPromptContainer,
  ApiMessageStack,
  FormControlV2,
} from "components";
import { FC } from "react";
import { useForm } from "react-hook-form";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { LeadIcon } from "enums/lead-icon.enum";
import { LEAD_ICON_OPTIONS } from "constants/lead-icons";
import { TLeadIconConfig } from "types/lead-icon.type";
import { getLeadTypeLabel } from "utils/lead-type.utils";
import { LeadIconSelectOption } from "components/select-input/custom-options/LeadIconSelectOption";
import { IGetOptionPropsData } from "components/select-input/SelectInputOptionsController";

type TFieldValues = {
  icon: LeadIcon;
};

const validationSchema = Yup.object().shape({
  icon: Yup.string().required("Please select an icon."),
});

interface IProps {
  closePrompt: () => void;
  leadIcon: TLeadIconConfig;
  sourceLabel: string;
  updateLeadIcon: (leadIconId: number, icon: LeadIcon) => Promise<void>;
}

export const EditLeadIconActionPrompt: FC<IProps> = ({
  closePrompt,
  leadIcon,
  sourceLabel,
  updateLeadIcon,
}) => {
  const toast = useToast();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<TFieldValues>({
    defaultValues: { icon: leadIcon.icon },
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: TFieldValues) => {
    try {
      await updateLeadIcon(leadIcon.id, data.icon);
      toast({
        ...DEFAULT_SUCCESS_TOAST_OPTIONS,
        description: <ApiMessageStack messageStack={"Lead icon updated."} />,
      });
      closePrompt();
    } catch (e) {
      toast({
        ...DEFAULT_ERROR_TOAST_OPTIONS,
        description: <ApiMessageStack messageStack={e.message} />,
      });
    }
  };

  return (
    <ActionPromptContainer
      header={"Edit lead icon"}
      body={
        <Box pt={5}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box mb={4}>
              <Text fontSize={"sm"} color={"leadpro.500"}>
                {sourceLabel}
                {" · "}
                <Box as={"span"} textTransform={"capitalize"}>
                  {getLeadTypeLabel(leadIcon.type)}
                </Box>
              </Text>
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"icon"}
                control={control}
                label={"Icon"}
                type={FormControlsTypeEnum.SINGLE_SELECT}
                additionalProps={{
                  options: LEAD_ICON_OPTIONS,
                  placeholder: "Select an icon",
                  optionComponent: (
                    optionProps: IGetOptionPropsData<LeadIcon>
                  ) => <LeadIconSelectOption optionProps={optionProps} />,
                }}
              />
            </Box>
            <Divider mb={4} />
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              <Button
                type={"submit"}
                colorScheme={"blue"}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </form>
        </Box>
      }
    />
  );
};
